import {useRef} from 'react';

import {Alert, Button, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {useParams} from 'react-router-dom';

import {useCustomerSubscriptions} from '../hooks/useCustomerSubscriptions.ts';
import {useCustomerSubscriptionsSelection} from '../hooks/useCustomerSubscriptionsSelection.ts';
import {useCustomerSubscriptionsTableColumns} from '../hooks/useCustomerSubscriptionsTableColumns.tsx';
import {useSubscriptionStatusChange} from '../hooks/useSubscriptionStatusChange.ts';
import {CustomerSubscription, CustomerSubscriptionStatus} from '../types';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

// The message for an empty table. THREE different situations (§3, §13), and they must
// not read alike: nothing matches the search, the status filter hides everything loaded,
// or there are no subscriptions at all.
const emptyState = (term: string, filters: Record<string, string>, moreToLoad: boolean) => {
    const more = moreToLoad
        ? ` ${I18n.get('More subscriptions may be on a page that has not been loaded yet.')}`
        : '';
    if (term) {
        return {
            color: 'gray',
            title: I18n.get('No matching subscriptions'),
            body: `${I18n.get('Nothing in the loaded subscriptions matches')} "${term}". ${I18n.get('Clear the search to see every loaded row.')}${more}`,
        };
    }
    if (Object.keys(filters).length > 0) {
        return {
            color: 'gray',
            title: I18n.get('No subscriptions match the filter'),
            body: `${I18n.get('The filter')} (${Object.entries(filters)
                                                         .map(([key, value]) => `${key}: ${value}`)
                                                         .join(', ')}) ${I18n.get('hides every loaded subscription. Open Filter and choose Clear to see all statuses.')}${more}`,
        };
    }
    return {
        color: 'blue',
        title: I18n.get('No subscriptions yet'),
        body: I18n.get('A subscription puts a customer on one or more prices. Open a customer and use Add Subscription to create the first one.'),
    };
};

export const RecordsTable = () => {
    // On a customer's page every row is that customer's, so the Customer column is left
    // out there and shown on the standalone index.
    const {id: customerId} = useParams();
    const {columns} = useCustomerSubscriptionsTableColumns(customerId ? 'customer' : 'reports');
    const {selectedRecords, setSelectedRecords} = useCustomerSubscriptionsSelection();
    const {changeStatus, pendingId} = useSubscriptionStatusChange();

    const {
        filteredItems,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchMoreRecords,
        handleFilterByKeyword
    } = useCustomerSubscriptions();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Activate an Inactive subscription, Cancel an Active one. Not gated: the deployed AVP
    // schema has no Subscription actions, so a gate would hide it from every non-admin
    // (FNP-1884). Not sortable: there is nothing to sort by.
    const actionsColumn: DataTableColumn<CustomerSubscription> = {
        accessor: 'actions',
        title: I18n.get('Actions'),
        render: (record: CustomerSubscription) => {
            if (record.status === CustomerSubscriptionStatus.Inactive) {
                return (
                    <Button size='xs'
                            loading={pendingId === record.id}
                            aria-label={`${I18n.get('Activate')} ${record.id}`}
                            onClick={() => changeStatus(record)}>
                        {I18n.get('Activate')}
                    </Button>
                );
            }
            if (record.status === CustomerSubscriptionStatus.Active) {
                return (
                    <Button size='xs' color='red'
                            loading={pendingId === record.id}
                            aria-label={`${I18n.get('Cancel')} ${record.id}`}
                            onClick={() => changeStatus(record)}>
                        {I18n.get('Cancel')}
                    </Button>
                );
            }
            return null;
        },
    };

    const term = keyword.trim();
    const empty = emptyState(term, filters, nextToken !== null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load subscriptions')}>
                    {I18n.get('The billing API did not return the subscriptions list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<CustomerSubscription>
                columns={[...columns, actionsColumn]}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchMoreRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              Gated on !loading so it does not flash during the first fetch, and on !isError
              so a failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={empty.color} title={empty.title}>
                    {empty.body}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={filteredItems.length}
                filterTerm={keyword}
                noun={'subscription'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchMoreRecords}
            />
        </SimpleGrid>
    );
};
